import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { QrCode, Star, TrendingUp } from "lucide-react";

interface StaffLeaderboardEntry {
  staffId: string;
  staffName: string;
  staffSlug: string;
  reviewCount: number;
  avgRating: number;
  requestsSent: number;
  responseRate: number;
}

interface LeaderboardTableProps {
  leaderboard: StaffLeaderboardEntry[];
  businessSlug?: string;
  isLoading?: boolean;
}

function getRankBadge(rank: number): string {
  switch (rank) {
    case 1:
      return "🥇";
    case 2:
      return "🥈";
    case 3:
      return "🥉";
    default:
      return `${rank}`;
  }
}

export function LeaderboardTable({ leaderboard, businessSlug, isLoading }: LeaderboardTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="mb-2">No staff members yet.</p>
            <Link href="/dashboard/staff">
              <Button variant="outline" size="sm">
                Add Team Members
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  const maxReviewCount = Math.max(...leaderboard.map((s) => s.reviewCount), 1);

  return (
    <div className="space-y-4">
      {leaderboard.map((staff, index) => {
        const rank = index + 1;
        const barWidth = (staff.reviewCount / maxReviewCount) * 100;
        const isTopThree = rank <= 3;

        return (
          <Card
            key={staff.staffId}
            className={`transition-all ${
              isTopThree ? "border-primary/30 shadow-sm" : ""
            }`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Rank Badge */}
                <div
                  className={`flex items-center justify-center h-10 w-10 rounded-full flex-shrink-0 ${
                    isTopThree
                      ? "bg-primary/10 text-xl"
                      : "bg-muted text-muted-foreground font-medium"
                  }`}
                >
                  {getRankBadge(rank)}
                </div>

                {/* Staff Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-semibold truncate">{staff.staffName}</h3>
                    {businessSlug && (
                      <Link href={`/dashboard/qr-codes?staff=${staff.staffSlug}`}>
                        <Button variant="ghost" size="sm" className="h-8 px-2">
                          <QrCode className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">QR Code</span>
                        </Button>
                      </Link>
                    )}
                  </div>

                  {/* Stats Row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mb-3">
                    <span className="font-medium text-foreground">
                      {staff.reviewCount} review{staff.reviewCount !== 1 ? "s" : ""}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                      {staff.avgRating.toFixed(1)}
                    </span>
                    {staff.requestsSent > 0 && (
                      <span>{staff.responseRate}% response rate</span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <Progress
                    value={barWidth}
                    className={`h-2 ${
                      rank === 1
                        ? "[&>div]:bg-yellow-500"
                        : rank === 2
                        ? "[&>div]:bg-slate-400"
                        : rank === 3
                        ? "[&>div]:bg-amber-600"
                        : ""
                    }`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
